import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { _className, EditableDBItemV3, LL_V_L, ModuleFE_AppConfig, stopPropagation } from '@nu-art/thunderstorm-frontend/index';
import { RoutePermissions } from '@nu-art/permissions-frontend/index';
import { TS_HorizontalWorkspace } from '@nu-art/ts-workspace-frontend/index';
import { workspaceKey_OrganizationEditor } from './workspace-config.js';
import { Dialog_Confirmation, dispatch_searchResultSelected, Page_BaseManagerUI_SingleType_Proto, Renderer_SearchResult, SearchItemV3_With_ID, SearchSelector_BaseManagerUI } from '@app/styles';
import { ModuleFE_Variable, QPageMessage } from '@app/hcs-frontend';
import { Editor_Organization } from './presenter/Editor_Organization.js';
import { MUSTNeverHappenException, voidFunction } from '@nu-art/ts-common';
import './Page_OrganizationEditor.scss';
import { ModulePackFE_Organization } from '../../module-pack.js';
import { ModuleFE_Action, ModuleFE_Pathway } from '@app/pathway-frontend';
import { ModuleFE_Organization } from '../../_entity/organization/index.js';
const EditorKey_Organization = 'organization-editor';
const modulesThisPageUses = [
    ...ModulePackFE_Organization,
    ModuleFE_Action,
    ModuleFE_Variable,
    ModuleFE_Pathway,
    ModuleFE_AppConfig,
];
export class Page_OrganizationEditor extends Page_BaseManagerUI_SingleType_Proto {
    // ######################## Static ########################
    static Route = {
        key: EditorKey_Organization,
        path: 'organization-v2',
        Component: this,
        enabled: RoutePermissions(ModuleFE_Organization.permissions.ui.Release),
        modulesToAwait: modulesThisPageUses,
    };
    static searchItemKey_Organization = ModuleFE_Organization.dbDef.dbKey;
    static SearchableItem_Organization = SearchItemV3_With_ID({
        module: ModuleFE_Organization,
        type: Page_OrganizationEditor.searchItemKey_Organization,
        route: Page_OrganizationEditor.Route,
        entityName: ModuleFE_Organization.dbDef.entityName,
        entityLabel: 'Organization',
        groupId: ['Organizations'],
        tags: [],
        filter: item => !item.systemOrganization,
        mapper: (item) => [item.name, ...item.webDomains],
        renderer: (item, selected, onClickCallback) => {
            return _jsx(Renderer_SearchResult, { itemId: item._id, itemKey: Page_OrganizationEditor.searchItemKey_Organization, itemLabel: item.name, entityLabel: 'Organization', hierarchy: 'Organizations', selected: selected, onClickCallback: onClickCallback });
        },
        resultRenderer: (item) => {
            const organization = ModuleFE_Organization.cache.unique(item);
            return _jsx(Editor_Organization, { editable: new EditableDBItemV3(organization, ModuleFE_Organization).setAutoSave(true), deselectOrganization: voidFunction, deleteOrganization: voidFunction }, organization._id);
        }
    });
    static SearchableItem_OrganizationV2 = SearchItemV3_With_ID({
        ...Page_OrganizationEditor.SearchableItem_Organization,
        renderer: (item, selected, onClickCallback) => {
            const className = _className('search-result', 'organization__search-item', selected && 'selected');
            const addressString = `${item.address.city}${item.address.state ? `, ${item.address.state.initials ?? item.address.state.label}` : ''}`;
            return _jsxs(LL_V_L, { className: className, onClick: e => {
                    stopPropagation(e);
                    onClickCallback?.();
                    dispatch_searchResultSelected.dispatchUI(item._id, Page_OrganizationEditor.searchItemKey_Organization);
                }, children: [item.alias?.length ? _jsx("div", { children: item.alias }) : null, _jsx("div", { children: item.name }), _jsx("div", { children: addressString })] });
        }
    });
    static defaultProps = {
        searchItems: [Page_OrganizationEditor.SearchableItem_OrganizationV2]
    };
    // ######################## Abstract Implementations ########################
    getInitialObject() {
        return {};
    }
    workspace = {
        id: EditorKey_Organization,
        key: workspaceKey_OrganizationEditor,
        renderers: {
            horizontalSpace: TS_HorizontalWorkspace,
        },
        instances: {
            selector: this.renderSelector.bind(this),
            presenter: this.renderPresenter.bind(this),
        }
    };
    // ######################## Logic ########################
    onCreateNewOrganization = () => {
        this.clearSelectedResult();
        const newOrg = this.getEditableItem();
        newOrg.set('address', {});
        this.setState({
            selectedItem: newOrg,
            selectedResult: undefined
        });
    };
    deleteOrganization = () => {
        const org = this.state.selectedItem;
        if (!org)
            throw new MUSTNeverHappenException('Triggered deleting org without selected org');
        Dialog_Confirmation.show({
            header: 'Are you sure?',
            content: _jsxs(_Fragment, { children: [_jsx("div", { children: "You are about to delete an organization" }), _jsx("div", { children: org.item.name })] }),
            buttons: [
                {
                    label: 'Yes, Delete', isAsync: true, action: async () => {
                        await ModuleFE_Organization._v1.deleteOrganization({ organizationId: org.item._id }).executeSync();
                        this.deselectOrganization();
                    }
                },
                { label: 'Don\'t Delete', isPreferred: true, action: voidFunction }
            ]
        });
    };
    deselectOrganization = () => {
        this.clearSelectedResult();
        this.setState({
            selectedItem: undefined,
            selectedResult: undefined
        });
    };
    // ######################## Render ########################
    renderSelector() {
        return _jsx(SearchSelector_BaseManagerUI, { selectedItem: this.state.selectedResult, onSearchItemSelected: (item) => this.onSearchResultSelected(item.id, item.type), searchItems: [Page_OrganizationEditor.SearchableItem_OrganizationV2], buttons: [{
                    label: 'Add Organizations',
                    action: this.onCreateNewOrganization
                }] });
    }
    renderPresenter() {
        const selectedItem = this.state.selectedItem;
        if (!selectedItem)
            return _jsx(QPageMessage, { message: 'Organization Manager' });
        return _jsx(Editor_Organization, { editable: selectedItem, deleteOrganization: this.deleteOrganization, deselectOrganization: this.deselectOrganization }, selectedItem.item._id);
    }
}
//# sourceMappingURL=Page_OrganizationEditor.js.map